import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import type { Notification } from '../../../shared/types';

export function NotificationBell() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    const res = await api.get<{ count: number }>('/notifications/unread-count');
    if (res.success) setUnreadCount(res.data.count);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleOpen() {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    const res = await api.get<{ notifications: Notification[]; total: number }>('/notifications?limit=8');
    if (res.success) setNotifications(res.data.notifications);
    setLoading(false);
  }

  async function handleMarkAllRead() {
    await api.post('/notifications/mark-all-read', {});
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
  }

  async function handleClickNotification(n: Notification) {
    if (!n.is_read) {
      await api.post(`/notifications/${n.id}/read`, {});
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, is_read: 1 } : item));
    }
    setOpen(false);
    if (n.project_id) {
      navigate(`/projects/${n.project_id}`);
    }
  }

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  const typeIcons: Record<string, string> = {
    gate_approved: 'text-success',
    gate_rejected: 'text-error',
    gate_approval_requested: 'text-warning',
    phase_transition: 'text-teal',
    decision_created: 'text-info',
    decision_approved: 'text-success',
    decision_rejected: 'text-error',
    hat_assigned: 'text-purple-600',
    hat_removed: 'text-text-muted',
    artifact_shared: 'text-teal',
    review_cycle_due: 'text-warning',
    participant_added: 'text-info',
    health_update: 'text-warning',
    mention: 'text-teal',
    system: 'text-text-muted',
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center rounded-md text-text-muted transition-colors hover:text-text-primary"
        aria-label="Notifications"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 bottom-full mb-2 w-80 sm:w-96 rounded-lg border border-border bg-surface shadow-lg z-50">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-[11px] text-teal hover:text-teal-dark transition-colors"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="text-[11px] text-text-muted hover:text-text-primary transition-colors"
              >
                View all
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-text-muted">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClickNotification(n)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-alt ${!n.is_read ? 'bg-teal/5' : ''}`}
                >
                  <div className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${!n.is_read ? 'bg-teal' : 'bg-transparent'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs leading-tight ${!n.is_read ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="mt-0.5 text-[11px] text-text-muted line-clamp-2">{n.body}</p>
                    )}
                    <div className="mt-1 flex items-center gap-2">
                      {n.project_name && (
                        <span className="text-[10px] text-text-muted">{n.project_name}</span>
                      )}
                      <span className={`text-[10px] ${typeIcons[n.notification_type] || 'text-text-muted'}`}>
                        {timeAgo(n.created_at)}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
